import React from 'react';
import { ArrowRight } from 'lucide-react';
import { cn } from '../../utils/cn';

export interface StatCardProps {
  label: string;
  value: React.ReactNode;
  detail?: React.ReactNode;
  // Kept for existing callers; stats read as plain numbers, so icons are not drawn.
  icon?: React.ReactNode;
  tone?: 'academy' | 'action' | 'warning' | 'danger';
  onClick?: () => void;
  className?: string;
}

// Wrap a group of StatCards in this so they read as one strip with hairline dividers.
export const statGroupClassName = 'grid gap-px overflow-hidden rounded-lg border border-ink-200 bg-ink-200';

const valueTone = {
  academy: 'text-ink-900',
  action: 'text-success-700',
  warning: 'text-warning-700',
  danger: 'text-danger-700',
};

export function StatCard({ label, value, detail, tone = 'academy', onClick, className }: StatCardProps) {
  const content = (
    <>
      <div className="min-w-0 flex-1">
        <p className="text-sm leading-snug text-ink-500">{label}</p>
        <div className={cn('mt-0.5 text-2xl font-semibold tabular-nums tracking-tight', valueTone[tone])}>{value}</div>
        {detail ? <div className="mt-0.5 truncate text-xs text-ink-500">{detail}</div> : null}
      </div>
      {onClick ? <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-ink-300" /> : null}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex w-full cursor-pointer items-start gap-3 bg-white px-4 py-3 text-left transition-colors hover:bg-ink-50',
          className,
        )}
      >
        {content}
      </button>
    );
  }

  return <div className={cn('flex items-start gap-3 bg-white px-4 py-3', className)}>{content}</div>;
}
